'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { SHOPIFY_REQUIRED_SCOPES, SHOPIFY_TUTORIAL_ASSET_BASE_PATH } from '@/lib/shopify-constants';
import { cn } from '@/lib/utils';

interface ShopifyScopeUpdateTutorialProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateIntegration: () => void;
  canUpdateIntegration: boolean;
  missingScopes: string[];
}

interface StepDefinition {
  titleKey: string;
  bodyKey: string;
  image?: string;
}

const DEV_DASHBOARD_URL = 'https://dev.shopify.com/dashboard';
const ASSET_BASE = `${SHOPIFY_TUTORIAL_ASSET_BASE_PATH}/scope-update`;

const STEPS: StepDefinition[] = [
  { titleKey: 'shopify.scopeUpdate.step1Title', bodyKey: 'shopify.scopeUpdate.step1Body' },
  {
    titleKey: 'shopify.scopeUpdate.step2Title',
    bodyKey: 'shopify.scopeUpdate.step2Body',
    image: `${ASSET_BASE}/step2-select-app.png`,
  },
  {
    titleKey: 'shopify.scopeUpdate.step3Title',
    bodyKey: 'shopify.scopeUpdate.step3Body',
    image: `${ASSET_BASE}/step3-new-version.png`,
  },
  {
    titleKey: 'shopify.scopeUpdate.step4Title',
    bodyKey: 'shopify.scopeUpdate.step4Body',
    image: `${ASSET_BASE}/step4-scopes.png`,
  },
  { titleKey: 'shopify.scopeUpdate.step5Title', bodyKey: 'shopify.scopeUpdate.step5Body' },
];

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  const { t } = useTranslation('integrations');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t('toasts.copied', { label }));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('toasts.failedToCopy'));
    }
  };

  return (
    <div className="flex items-center gap-2 rounded-md border bg-gray-50 p-2">
      <code className="flex-1 overflow-x-auto whitespace-nowrap text-xs text-gray-700">{text}</code>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="h-6 w-6 shrink-0 p-0"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export function ShopifyScopeUpdateTutorial({
  open,
  onOpenChange,
  onUpdateIntegration,
  canUpdateIntegration,
  missingScopes,
}: ShopifyScopeUpdateTutorialProps) {
  const { t } = useTranslation('integrations');
  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = STEPS.length;
  const step = STEPS[currentStep - 1];
  const isLastStep = currentStep === totalSteps;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCurrentStep(1);
    }
    onOpenChange(next);
  };

  const goToStep = (n: number) => setCurrentStep(Math.max(1, Math.min(n, totalSteps)));
  const next = () => goToStep(currentStep + 1);
  const prev = () => goToStep(currentStep - 1);

  const handleUpdateClick = () => {
    onUpdateIntegration();
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex h-[95vh] w-[95vw] max-w-[95vw] flex-col overflow-y-auto sm:max-w-[95vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {t('shopify.scopeUpdate.modalTitle')}
          </DialogTitle>
          <DialogDescription>{t('shopify.scopeUpdate.modalDescription')}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex min-h-0 flex-1 flex-col space-y-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
              {currentStep}
            </span>
            <span>{t('shopify.scopeUpdate.stepOf', { current: currentStep, total: totalSteps })}</span>
          </div>

          <h3 className="text-base font-semibold text-gray-900">{t(step.titleKey)}</h3>
          <p className="text-sm text-gray-600">{t(step.bodyKey)}</p>

          {currentStep === 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => window.open(DEV_DASHBOARD_URL, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('shopify.scopeUpdate.step1OpenDashboard')}
            </Button>
          )}

          {step.image && (
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-md border bg-gray-50">
              <img src={step.image} alt={t(step.titleKey)} className="max-h-full max-w-full object-contain" />
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-3">
              {missingScopes.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-700">
                    {t('shopify.scopeUpdate.step4MissingLabel')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {missingScopes.map((scope) => (
                      <span
                        key={scope}
                        className="rounded-full bg-red-50 px-2 py-0.5 font-mono text-[10px] text-red-700"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="mb-1 text-xs font-medium text-gray-700">
                  {t('shopify.scopeUpdate.step4ScopesLabel')}
                </p>
                <CopyButton text={SHOPIFY_REQUIRED_SCOPES} label={t('shopifyTutorial.copyLabels.scopes')} />
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <Button
              type="button"
              className="w-full bg-[#96bf48] hover:bg-[#7a9e3a]"
              onClick={handleUpdateClick}
              disabled={!canUpdateIntegration}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('shopify.scopeUpdate.step5Cta')}
            </Button>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 border-t pt-4">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goToStep(i + 1)}
                className={cn(
                  'h-2 rounded-full transition-all',
                  currentStep === i + 1 ? 'w-6 bg-blue-600' : 'w-2 bg-gray-300 hover:bg-gray-400',
                )}
                aria-label={t('shopify.scopeUpdate.stepOf', { current: i + 1, total: totalSteps })}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={prev}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              {t('shopify.scopeUpdate.previous')}
            </Button>
            {!isLastStep && (
              <Button type="button" size="sm" onClick={next}>
                {t('shopify.scopeUpdate.next')}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
